import pluralize from 'pluralize-esm';
import { ServerRequest } from './ServerRequest';
import { stripIndent } from 'common-tags';

const SUPPORT_EMAILS = ['support+updatelimit@katapultengineering.com'];

function GetUpdateOptions() {
  // Get the database write options from the global app-configuration/config file into which app-configuration injects the white label data as `firebaseData`.
  const databaseWriteOptionsFromAppConfigurationConfig = globalThis.config?.firebaseData?.database_write_options;
  // Get the database write options from the worker's global scope which is set via a message posted to the worker and handled in `BlueCollar.js`.
  const databaseWriteOptionsFromWorkerDatabaseWriteOptions = globalThis.databaseWriteOptions;
  const databaseWriteOptions = databaseWriteOptionsFromAppConfigurationConfig ?? databaseWriteOptionsFromWorkerDatabaseWriteOptions;
  return databaseWriteOptions?.update_options;
}

async function NotifySupportOfUpdateSize(update, { updateSizeError = new Error(), shouldBlockUpdate = false }) {
  const numberOfPaths = Object.keys(update).length;
  const { stack } = updateSizeError;
  const linkToPage = globalThis.location.href;
  const { email: userEmail } = globalThis.firebase.auth().currentUser ?? {};
  const { userGroup: userActiveCompany } = (await globalThis.firebase.auth().currentUser?.getIdTokenResult())?.claims ?? {};

  const body = stripIndent`
    The following large update was ${shouldBlockUpdate ? 'detected and blocked' : 'detected'}:
    
    User: ${userEmail}
    Company: ${userActiveCompany}
    Link: ${linkToPage}
    Update Size: ${numberOfPaths} ${pluralize('path', numberOfPaths)}
    Call Stack: ${stack}
  `;

  const emailConfig = {
    addresses: SUPPORT_EMAILS,
    subject: `${shouldBlockUpdate ? '🚨' : '⚠️'} Large Update ${shouldBlockUpdate ? 'Blocked' : 'Detected'}`,
    body
  };
  await ServerRequest('email', emailConfig);
}

export async function ShowLargeUpdateBlockedDialog() {
  const { KatapultDialog } = await import('../elements/katapult-elements/katapult-dialog.js');
  KatapultDialog.alert({
    dialog: {
      closeOnOutsideClick: false,
      modal: true,
      icon: 'block',
      color: 'var(--sl-color-danger)',
      title: 'Database Write Blocked'
    },
    text: 'The server has blocked an attempt to write too much data in a single request.\n\nPlease try again using a smaller set of data.\n\nFor more information, please contact support@katapultengineering.com'
  });
  return;
}

function NotifyUserOfBlockedUpdate() {
  const inBrowserContext = globalThis.window != null;
  if (inBrowserContext) {
    ShowLargeUpdateBlockedDialog();
    return;
  }

  const inWorkerContext = !inBrowserContext && globalThis.postMessage != null;
  if (inWorkerContext) {
    globalThis.postMessage({ type: 'large-update-blocked' });
  }
}

/**
 * Blocks large updates to the database and notifies support.
 * @param {any} update
 * @returns {void}
 * @throws {Error} If the update size exceeds the size threshold.
 */
export function BlockLargeUpdates(update) {
  const updateOptions = GetUpdateOptions();
  const blockAndNotifySupportThreshold = updateOptions?.block_and_notify_support_threshold;
  const notifySupportThreshold = updateOptions?.notify_support_threshold;

  const numberOfPaths = Object.keys(update ?? {}).length;

  const updateSizeExceedsBlockAndNotifySupportThreshold =
    blockAndNotifySupportThreshold != null && numberOfPaths > blockAndNotifySupportThreshold;
  const updateSizeExceedsNotifySupportThreshold = notifySupportThreshold != null && numberOfPaths > notifySupportThreshold;

  const shouldBlockUpdate = updateSizeExceedsBlockAndNotifySupportThreshold;
  const shouldNotifySupport = updateSizeExceedsBlockAndNotifySupportThreshold || updateSizeExceedsNotifySupportThreshold;

  if (!shouldBlockUpdate && !shouldNotifySupport) return;

  const relevantThreshold = shouldBlockUpdate ? blockAndNotifySupportThreshold : notifySupportThreshold;
  const updateSizeError = new Error(
    `Update size exceeds ${shouldBlockUpdate ? 'limit' : 'warning threshold'} of ${relevantThreshold} ${pluralize('path', relevantThreshold)}`
  );

  if (shouldNotifySupport) NotifySupportOfUpdateSize(update, { updateSizeError, shouldBlockUpdate });

  if (shouldBlockUpdate) {
    NotifyUserOfBlockedUpdate();
    throw updateSizeError;
  }
}
